/**
 * Pure, DI-free decimal (SI) byte formatting.
 *
 * The backend reports every size in decimal — divide by 1,000,000 for MB — on
 * every client, always. It used to sniff `User-Agent` and switch to binary
 * (1024) arithmetic for Windows; that was removed on 2026-07-10, along with
 * this codebase's matching `FileSizeModeService`.
 *
 * A consequence, stated here so nobody "fixes" it: our "MB" will disagree with
 * Windows File Explorer's "MB" for the same file. Explorer divides by 1,048,576
 * and labels the result "MB", which is really a mebibyte. A 52,428,800-byte file
 * reads "50.0 MB" in Explorer and "52.4 MB" here. Both are correct; only one
 * matches the server's 50,000,000-byte cap.
 *
 * Labels are always SI-flavoured (`bytes / KB / MB / GB`). Never render IEC
 * labels (`KiB / MiB / GiB`) — end users do not recognise them.
 *
 * KNOWN LIMITATION (accepted, see the design spec §6): `formatFileSize` rounds
 * to nearest, so a file between 50,000,001 and 50,049,999 bytes formats as
 * exactly "50 MB" at precision 1 — the same string as the cap. An oversize
 * message quoting both would read "exceeds the 50 MB limit. Your file: 50 MB."
 * No precision setting fixes this (round-to-nearest maps just-over-cap onto the
 * cap). We accept it because the only place `file.size` is echoed is the 413
 * toast, and a 413 for a file in that band is vanishingly rare. Do NOT add a
 * `ceil` variant without re-reading that spec section.
 *
 * This file imports nothing. Keep it that way — it is what lets the util be
 * unit-tested without TestBed and consumed from a pure validator.
 */

export const SI_KB = 1_000;
export const SI_MB = 1_000_000;
export const SI_GB = 1_000_000_000;

/** Widest precision we allow before `10 ** p` starts shedding float accuracy. */
const MAX_PRECISION = 6;

/**
 * Clamps a caller-supplied decimal precision into `[0, 6]`.
 *
 * Negative precision is meaningless; `> 6` produces useless tail zeros while
 * `10 ** precision` loses accuracy. A `NaN` (e.g. a hand-edited environment
 * file) would otherwise poison every subsequent `.toFixed` into `"NaN MB"`.
 *
 * `NaN` is the only value needing a guard: `Math.trunc` passes the infinities
 * through unchanged, and `Math.min`/`Math.max` clamp them to the range ends.
 */
export function clampFileSizePrecision(raw: number): number {
  if (Number.isNaN(raw)) {
    return 0;
  }
  return Math.max(0, Math.min(MAX_PRECISION, Math.trunc(raw)));
}

/**
 * Formats a byte count using decimal (SI) divisors.
 *
 * Values below 1 KB always render as a whole number of bytes regardless of
 * `precision`. A rounded value that lands on a whole number drops its trailing
 * `.0` / `.00`, so 50,000,000 bytes reads `"50 MB"` rather than `"50.0 MB"`.
 */
export function formatFileSize(
  bytes: number | null | undefined,
  precision: number,
): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) {
    return '0 bytes';
  }

  const clampedPrecision = clampFileSizePrecision(precision);

  if (bytes < SI_KB) {
    return `${Math.round(bytes)} bytes`;
  }
  if (bytes < SI_MB) {
    return `${roundToPrecision(bytes / SI_KB, clampedPrecision)} KB`;
  }
  if (bytes < SI_GB) {
    return `${roundToPrecision(bytes / SI_MB, clampedPrecision)} MB`;
  }
  return `${roundToPrecision(bytes / SI_GB, clampedPrecision)} GB`;
}

/**
 * Round-half-away-from-zero at `precision`, then strip trailing zeros.
 *
 * `precision` is a MAXIMUM, not fixed padding (product-owner decision): at
 * precision 1 a 50,000,000-byte file reads "50 MB", not "50.0 MB"; at precision 2
 * a 49,500,000-byte file reads "49.5 MB", not "49.50 MB".
 *
 * Uses `Math.round` rather than `.toFixed(p)` alone so the rounding behaviour
 * cannot drift on an engine that interprets `.toFixed` with banker's rounding.
 * Non-negative inputs only — `formatFileSize` guards negatives before this runs.
 *
 * ORDER IS LOAD-BEARING: the `Number.isInteger` early-return must come FIRST, and
 * neither half may be collapsed into the other. The regex strips a trailing run of
 * zeros with an only-optional preceding dot, so it mauls any string that has no
 * decimal point — and `.toFixed(0)` never emits one. Route a whole number through
 * the regex and "50" becomes "5", "100" becomes "1". The early-return is the only
 * thing guaranteeing the regex sees a decimal point and a non-zero digit after it.
 */
function roundToPrecision(value: number, precision: number): string {
  const factor = 10 ** precision;
  const rounded = Math.round(value * factor) / factor;
  if (Number.isInteger(rounded)) {
    return rounded.toString();
  }
  return rounded.toFixed(precision).replace(/\.?0+$/, '');
}
